import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useHistory, useParams } from "react-router-dom";
import ReactLoading from "react-loading";
import { darkToast, toast, ToastContainer } from "../../Components/Toast";
import { serverUrl } from "../../Utils/server";

function slugify(value = "") {
  return String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function EditCatalogCategory() {
  const { categoryId } = useParams();
  const history = useHistory();

  const [categories, setCategories] = useState([]);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [image, setImage] = useState("");
  const [icon, setIcon] = useState("");
  const [parentId, setParentId] = useState("");
  const [sortOrder, setSortOrder] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [isActive, setIsActive] = useState(true);
  const [loadingPage, setLoadingPage] = useState(true);
  const [loadingSave, setLoadingSave] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [slugTouched, setSlugTouched] = useState(true);

  const token = localStorage.getItem("token");

  useEffect(() => {
    const load = async () => {
      try {
        const [listResponse, itemResponse] = await Promise.all([
          axios({
            method: "get",
            url: `${serverUrl}/catalog-categories/`,
            headers: {
              "Content-Type": "application/json",
              Authorization: `JWT ${token}`,
            },
          }),
          axios({
            method: "get",
            url: `${serverUrl}/catalog-categories/item/${categoryId}`,
            headers: {
              "Content-Type": "application/json",
              Authorization: `JWT ${token}`,
            },
          }),
        ]);

        const rows = Array.isArray(listResponse.data) ? listResponse.data : [];
        const item = itemResponse.data || {};

        setCategories(rows);
        setName(item.name || "");
        setSlug(item.slug || "");
        setDescription(item.description || "");
        setImage(item.image || "");
        setIcon(item.icon || "");
        setParentId(item.parentId || "");
        setSortOrder(item.sortOrder || 0);
        setIsVisible(typeof item.isVisible === "boolean" ? item.isVisible : true);
        setIsActive(typeof item.isActive === "boolean" ? item.isActive : true);
      } catch (error) {
        console.error("EditCatalogCategory load error:", error);
        toast("Failed to load category info", darkToast);
      } finally {
        setLoadingPage(false);
      }
    };

    load();
  }, [categoryId, token]);

  const availableParents = useMemo(() => {
    return categories
      .filter((row) => String(row._id) !== String(categoryId))
      .sort((a, b) => {
        const levelA = Number(a?.level || 1);
        const levelB = Number(b?.level || 1);
        if (levelA !== levelB) return levelA - levelB;
        return String(a?.name || "").localeCompare(String(b?.name || ""));
      });
  }, [categories, categoryId]);

  const selectedParent = useMemo(() => {
    return categories.find((item) => String(item?._id) === String(parentId)) || null;
  }, [categories, parentId]);

  const expectedLevel = selectedParent ? Number(selectedParent.level || 1) + 1 : 1;

  const onChangeName = (value) => {
    setName(value);
    if (!slugTouched) {
      setSlug(slugify(value));
    }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);

    try {
      const formData = new FormData();
      formData.append("image", file);
      formData.append("folder", "categories");

      const response = await axios.post(`${serverUrl}/upload/image`, formData, {
        headers: {
          Authorization: `JWT ${token}`,
        },
        timeout: 45000,
      });

      const url = response?.data?.url;
      if (!url) {
        throw new Error("Upload backend did not return url");
      }

      setImage(url);
      toast("Image uploaded successfully", darkToast);
    } catch (error) {
      console.error("EditCatalogCategory uploadImage error:", error);
      const msg =
        error?.response?.data?.msg ||
        error?.response?.data ||
        error?.message ||
        "Error uploading image";
      toast(String(msg), darkToast);
    } finally {
      setUploadingImage(false);
    }
  };

  const save = async () => {
    if (!name.trim()) return toast("Category name is required", darkToast);

    if (selectedParent && Number(selectedParent.level || 1) >= 3) {
      return toast("A level 3 category cannot have children", darkToast);
    }

    setLoadingSave(true);

    try {
      const response = await axios({
        method: "post",
        url: `${serverUrl}/catalog-categories/update`,
        headers: {
          "Content-Type": "application/json",
          Authorization: `JWT ${token}`,
        },
        data: {
          categoryId,
          query: {
            name: name.trim(),
            slug: slugify(slug || name),
            description: description.trim(),
            image: image.trim(),
            icon: icon.trim(),
            parentId: parentId || null,
            sortOrder: Number(sortOrder || 0),
            isVisible: Boolean(isVisible),
            isActive: Boolean(isActive),
          },
        },
      });

      if (response.status === 200) {
        toast("Catalog category updated!", darkToast);
        setTimeout(() => {
          history.push("/categories");
        }, 800);
      }
    } catch (error) {
      console.error("EditCatalogCategory save error:", error);
      const msg =
        error?.response?.data?.msg ||
        error?.response?.data ||
        "Something went wrong";
      toast(String(msg), darkToast);
    } finally {
      setLoadingSave(false);
    }
  };

  return (
    <div className="content-body">
      <div className="content-title">
        <p>Edit Catalog Category</p>
      </div>

      {loadingPage ? (
        <div style={{ margin: "2rem 0" }}>
          <ReactLoading
            type={"spinningBubbles"}
            color={"var(--primaryColor)"}
            width={"2rem"}
            height={"2rem"}
          />
        </div>
      ) : (
        <div className="form">
          <p>Category Name</p>
          <input
            type="text"
            placeholder="Category Name"
            value={name}
            onChange={(e) => onChangeName(e.target.value)}
          />

          <p>Slug</p>
          <input
            type="text"
            placeholder="category-slug"
            value={slug}
            onChange={(e) => {
              setSlugTouched(true);
              setSlug(slugify(e.target.value));
            }}
          />

          <p>Description</p>
          <textarea
            placeholder="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          <p>Image</p>
          {image ? (
            <img
              src={image}
              alt="Category"
              style={{
                width: "20%",
                marginTop: "1rem",
                marginBottom: "1rem",
                objectFit: "contain",
              }}
            />
          ) : null}

          <p>Replace Image</p>
          {uploadingImage ? (
            <div style={{ margin: "1rem" }}>
              <ReactLoading
                type={"spinningBubbles"}
                color={"var(--primaryColor)"}
                width={"2rem"}
                height={"2rem"}
              />
            </div>
          ) : (
            <input
              type="file"
              onChange={uploadImage}
            />
          )}

          {image ? (
            <button
              type="button"
              style={{ marginTop: "0.75rem", marginBottom: "1rem" }}
              onClick={() => setImage("")}
            >
              Remove Image
            </button>
          ) : null}

          <p>Image URL (optional/manual)</p>
          <input
            type="text"
            placeholder="https://..."
            value={image}
            onChange={(e) => setImage(e.target.value)}
          />

          <p>Icon</p>
          <input
            type="text"
            placeholder="Optional icon name"
            value={icon}
            onChange={(e) => setIcon(e.target.value)}
          />

          <p>Parent Category</p>
          <select
            value={parentId}
            onChange={(e) => setParentId(e.target.value)}
          >
            <option value="">— Root category (Level 1) —</option>
            {availableParents.map((row) => (
              <option key={row._id} value={row._id}>
                {`${"— ".repeat(Math.max((Number(row.level || 1) - 1), 0))}${row.name}`}
              </option>
            ))}
          </select>

          <p>Expected Level</p>
          <input type="text" value={expectedLevel} disabled />

          <p>Sort Order</p>
          <input
            type="number"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
          />

          <div style={{ display: "flex", gap: "1.5rem", marginTop: "1rem" }}>
            <label>
              <input
                type="checkbox"
                checked={isVisible}
                onChange={(e) => setIsVisible(e.target.checked)}
              />{" "}
              Visible
            </label>

            <label>
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />{" "}
              Active
            </label>
          </div>

          <div style={{ display: "flex", gap: "12px", marginTop: "1rem" }}>
            <button
              className={loadingSave ? "disabled" : ""}
              onClick={save}
            >
              {loadingSave ? (
                <ReactLoading
                  type={"spinningBubbles"}
                  color={"white"}
                  width={"1.2rem"}
                  height={"1.2rem"}
                />
              ) : (
                "Save"
              )}
            </button>

            <button
              type="button"
              onClick={() => history.push("/categories")}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
}

export default EditCatalogCategory;
